import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';
import BackgroundOverlay from './components/BackgroundOverlay';
import PreAuthHeader from './components/PreAuthHeader';
import ConcalLogo from '../../components/ConcalLogo';
import Icon from '../../components/AppIcon';

const Login = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true') {
      navigate('/contract-upload');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background with Overlay */}
      <BackgroundOverlay />
      {/* Pre-Authentication Header */}
      <PreAuthHeader />
      {/* Main Content */}
      <div className="relative z-content pt-16">
        <div className="min-h-screen flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Welcome Content with CONCAL Logo */}
              <div className="hidden lg:block">
                <div className="max-w-lg">
                  <div className="flex items-center justify-center lg:justify-start mb-8">
                    <ConcalLogo size="xl" className="w-auto h-20" />
                  </div>
                  
                  <h1 className="text-4xl font-heading font-semibold text-foreground mb-6 leading-tight">
                    Streamline Your Real Estate Contract Processing
                  </h1>
                  <p className="text-lg text-muted-foreground font-body leading-relaxed mb-8">
                    Automatically extract key dates, terms, and deadlines from sales contracts across different states. Generate professional client notification emails with calendar integration.
                  </p>
                  
                  {/* Key Features */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                        <Icon name="FileSearch" size={16} color="var(--color-success)" />
                      </div>
                      <span className="text-foreground font-body font-medium">
                        Multi-state contract format recognition
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                        <Icon name="Calendar" size={16} color="var(--color-success)" />
                      </div>
                      <span className="text-foreground font-body font-medium">
                        Automatic deadline extraction & calendar integration
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                        <Icon name="Mail" size={16} color="var(--color-success)" />
                      </div>
                      <span className="text-foreground font-body font-medium">
                        Professional client communication emails
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Login Form */}
              <div className="w-full">
                <LoginForm />
                
                {/* Mobile Welcome Content */}
                <div className="lg:hidden mt-8 text-center">
                  <div className="flex items-center justify-center mb-6">
                    <ConcalLogo size="large" className="w-auto h-16" />
                  </div>
                  <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                    Streamline Your Contract Processing
                  </h2>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    Automatically extract key dates and generate professional client emails with calendar integration.
                  </p>
                </div>
              </div>
            </div>

            {/* Security Badges */}
            <SecurityBadges />
          </div>
        </div>
      </div>
      {/* Footer - Updated to remove ContractParser Pro branding */}
      <footer className="relative z-content bg-card/80 backdrop-blur-md border-t border-border/50 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-muted-foreground font-body text-sm">
              © {new Date()?.getFullYear()} CONCAL. All rights reserved.
            </div>
            <div className="flex items-center space-x-6">
              <button className="text-muted-foreground hover:text-foreground font-body text-sm transition-colors duration-200">
                Privacy Policy
              </button>
              <button className="text-muted-foreground hover:text-foreground font-body text-sm transition-colors duration-200">
                Terms of Service
              </button>
              <button className="text-muted-foreground hover:text-foreground font-body text-sm transition-colors duration-200">
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
